import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    managementFeesApi,
    supportingDataApi,
    ManagementFeeRow,
} from "@/services/api";
import { formatNumber } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";
import { ColumnDef } from "@/components/utils/DataTable";
import DataTable from "@/components/utils/DataTable";
import InvestmentStrategySelect, { InvestmentStrategyOption } from "@/components/utils/InvestmentStrategySelect";
import { loadSchemeOptions } from "@/components/utils/filters";
import { FilterSelect, FilterAsyncSelect, FilterOption, Button } from "@/components/ui";
import { getCurrentFYQuarter, formatQuarterLabel, getRecentFYQuarters } from "@/utils/fy";
import { FEE_BUFFER_PCT } from "@/config/constants";
import FeeInvoiceFilesModal from "@/components/FeeInvoiceFilesModal";
import styles from "./ManagementFees.module.css";

type ClientOption = { value: string; label: string };

// Sentinel for "clients with no distributor" — distinct from "" (no filter applied)
const NO_DISTRIBUTOR = "__none__";

// Session key preserves filter state when navigating to a client detail page and back
const SESSION_KEY = "mgmt_fees_list_state";

// Columns carrying a total. Summed client-side from the filtered rows rather than
// taken from the API summary, so the totals track every filter.
const TOTAL_KEYS = [
    "nuvama_avg_aum", "nuvama_fee", "nuvama_distribution_fee", "nuvama_fee_gst",
    "holding_avg_aum", "holding_fee", "holding_distribution_fee", "holding_fee_gst",
    "cash_plus_liquid",
] as const;
type TotalKey = typeof TOTAL_KEYS[number];

const ManagementFees: React.FC = () => {
    const navigate = useNavigate();
    const [investmentStrategies, setInvestmentStrategies] = useState<InvestmentStrategyOption[]>([]);
    const [selectedStrategy, setSelectedStrategy] = useState<number | "">("");
    const [selectedScheme, setSelectedScheme] = useState<FilterOption | null>(null);
    const [selectedDistributor, setSelectedDistributor] = useState<string>("");
    const [selectedQuarter, setSelectedQuarter] = useState(getCurrentFYQuarter());
    const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
    const [results, setResults] = useState<ManagementFeeRow[]>([]);
    const [hasFetched, setHasFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<keyof ManagementFeeRow>("client_code");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [lowCashOnly, setLowCashOnly] = useState(false);
    const [fetchedQuarter, setFetchedQuarter] = useState<string | null>(null);
    const [showInvoiceFiles, setShowInvoiceFiles] = useState(false);
    const pageSize = 20;

    const quarterOptions = getRecentFYQuarters();
    // isCurrentQuarter is derived from the last *fetched* quarter, not the dropdown value.
    // If we used selectedQuarter, switching the dropdown (without hitting Search) would
    // immediately flip isCurrentQuarter, causing isLowCash to return false for all rows
    // and the at-risk badge to disappear — even though the displayed data hasn't changed.
    // fetchedQuarter is only set when a fetch completes, so the badge stays correct
    // until the user actually loads new data.
    const isCurrentQuarter = fetchedQuarter === getCurrentFYQuarter();

    const isLowCash = (row: ManagementFeeRow): boolean => {
        if (!isCurrentQuarter || row.cash_plus_liquid == null) return false;
        return (
            (row.nuvama_fee_gst != null && row.cash_plus_liquid < row.nuvama_fee_gst * (1 + FEE_BUFFER_PCT)) ||
            (row.holding_fee_gst != null && row.cash_plus_liquid < row.holding_fee_gst * (1 + FEE_BUFFER_PCT))
        );
    };

    // At-risk logic is PMS-only — AIF clients are not subject to the same cash sufficiency
    // check, so they should never be highlighted or counted as at risk.
    const isPmsCashAtRisk = useCallback(
        (row: ManagementFeeRow) => row.client_type === "PMS" && isLowCash(row),
        [isCurrentQuarter],
    );

    useEffect(() => {
        const saved = sessionStorage.getItem(SESSION_KEY);
        let hasSavedState = false;
        if (saved) {
            try {
                const s = JSON.parse(saved);
                if (s.selectedStrategy) setSelectedStrategy(s.selectedStrategy);
                if (s.selectedScheme) setSelectedScheme(s.selectedScheme);
                if (s.selectedDistributor) setSelectedDistributor(s.selectedDistributor);
                if (s.selectedQuarter) setSelectedQuarter(s.selectedQuarter);
                if (s.selectedClient) setSelectedClient(s.selectedClient);
                if (s.sortBy) setSortBy(s.sortBy);
                if (s.sortDir) setSortDir(s.sortDir);
                if (s.currentPage) setCurrentPage(s.currentPage);
                if (s.results) { setResults(s.results); setHasFetched(true); }
                if (s.fetchedQuarter) setFetchedQuarter(s.fetchedQuarter);
                if (s.lowCashOnly) setLowCashOnly(s.lowCashOnly);
                hasSavedState = true;
            } catch { /* ignore */ }
            sessionStorage.removeItem(SESSION_KEY);
        }
        loadInvestmentStrategies(!hasSavedState);
    }, []);

    const loadInvestmentStrategies = async (setDefault = false) => {
        try {
            const data = await supportingDataApi.getInvestmentStrategies({ params: { page_size: 100 } });
            const strategies = data.map((s: { id: number; name: string; code: string }) => ({ id: s.id, name: s.name, code: s.code }));
            setInvestmentStrategies(strategies);
            if (setDefault) {
                const coreDomestic = strategies.find(
                    (s: InvestmentStrategyOption) => s.name === "Core Domestic" || s.code === "Core Domestic"
                );
                if (coreDomestic) setSelectedStrategy(coreDomestic.id);
            }
        } catch {
            setError("Failed to load strategies");
        }
    };

    const fetchFees = async () => {
        setLoading(true);
        setError(null);
        try {
            const params: Parameters<typeof managementFeesApi.getQuarterlyFees>[0] = { quarter: selectedQuarter };
            if (selectedStrategy) params.investment_strategy = Number(selectedStrategy);
            if (selectedScheme) params.scheme = Number(selectedScheme.value);

            const response = await managementFeesApi.getQuarterlyFees(params);
            setResults(response.results);
            setFetchedQuarter(selectedQuarter);
            setCurrentPage(1);
            setHasFetched(true);
            setSelectedClient(null);
            setSelectedDistributor("");
        } catch (err) {
            const apiErr = err as { response?: { data?: { error?: string } } };
            setError(apiErr?.response?.data?.error || "Failed to fetch management fees");
            setResults([]);
            setHasFetched(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: string) => {
        const f = field as keyof ManagementFeeRow;
        if (sortBy === f) {
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(f);
            setSortDir("asc");
        }
        setCurrentPage(1);
    };

    const numericFields: (keyof ManagementFeeRow)[] = [
        "nuvama_fee", "nuvama_fee_gst", "holding_fee", "holding_fee_gst",
        "nuvama_avg_aum", "holding_avg_aum", "cash_plus_liquid",
        "distributor_share_pct", "nuvama_distribution_fee", "holding_distribution_fee",
    ];

    const atRiskCount = useMemo(() => results.filter(isPmsCashAtRisk).length, [results, isCurrentQuarter]);

    const clientOptions: ClientOption[] = useMemo(
        () => results.map(r => ({ value: r.client_code, label: `${r.client_code} — ${r.client_name}` })),
        [results]
    );

    const distributorOptions = useMemo(() => {
        const names = new Set<string>();
        let hasNoDistributor = false;
        results.forEach(r => {
            if (r.distributor_name) names.add(r.distributor_name);
            else hasNoDistributor = true;
        });
        return { names: Array.from(names).sort(), hasNoDistributor };
    }, [results]);

    const distributorSelectOptions: FilterOption[] = useMemo(() => [
        ...distributorOptions.names.map(name => ({ value: name, label: name })),
        ...(distributorOptions.hasNoDistributor
            ? [{ value: NO_DISTRIBUTOR, label: "No Distributor" }]
            : []),
    ], [distributorOptions]);

    const quarterSelectOptions: FilterOption[] = useMemo(
        () => quarterOptions.map(q => ({ value: q, label: formatQuarterLabel(q) })),
        [quarterOptions]
    );

    const filteredAndSorted = useMemo(() => {
        const filtered = results.filter(row => {
            const matchesClient = !selectedClient || row.client_code === selectedClient.value;
            const matchesDistributor = !selectedDistributor
                || (selectedDistributor === NO_DISTRIBUTOR ? !row.distributor_name : row.distributor_name === selectedDistributor);
            return matchesClient && matchesDistributor && (!lowCashOnly || isPmsCashAtRisk(row));
        });
        return filtered.sort((a, b) => {
            // Always show inactive clients last
            if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;

            let valA: any = a[sortBy];
            let valB: any = b[sortBy];
            if (numericFields.includes(sortBy)) {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
            } else {
                valA = String(valA || "").toLowerCase();
                valB = String(valB || "").toLowerCase();
            }
            if (valA < valB) return sortDir === "asc" ? -1 : 1;
            if (valA > valB) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [results, selectedClient, selectedDistributor, sortBy, sortDir, lowCashOnly, isCurrentQuarter]);

    const totalPages = Math.ceil(filteredAndSorted.length / pageSize);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredAndSorted.slice(start, start + pageSize);
    }, [filteredAndSorted, currentPage]);

    const handleRowClick = (row: ManagementFeeRow) => {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
            selectedStrategy, selectedScheme, selectedDistributor, selectedQuarter,
            selectedClient, sortBy, sortDir, currentPage, lowCashOnly, results, fetchedQuarter,
        }));
        navigate(`/management-fees/${row.client_code}`);
    };

    const fmt = (val: number | null) => (val != null ? formatNumber(val, 2) : "—");

    // Mirrors the on-screen column order. The export carries data rows only — a
    // totals row would be double-counted by anything that sums the file.
    const csvColumns: { header: string; value: (r: ManagementFeeRow) => string | number }[] = [
        { header: "Client Code", value: r => r.client_code },
        { header: "Client Name", value: r => r.client_name || "" },
        { header: "Scheme", value: r => r.scheme_code || "" },
        { header: "Distributor", value: r => r.distributor_name || "" },
        { header: "Mgmt Fee %", value: r => r.management_fee_pct ?? "" },
        { header: "Dist Share %", value: r => r.distributor_share_pct ?? "" },
        { header: "Chargeable AUM", value: r => r.nuvama_avg_aum ?? "" },
        { header: "Nuvama Fee", value: r => r.nuvama_fee ?? "" },
        { header: "Nuvama Dist Fee", value: r => r.nuvama_distribution_fee ?? 0 },
        { header: "Nuvama Fee (GST)", value: r => r.nuvama_fee_gst ?? "" },
        { header: "Holding AUM", value: r => r.holding_avg_aum ?? "" },
        { header: "Holding Fee", value: r => r.holding_fee ?? "" },
        { header: "Holding Dist Fee", value: r => r.holding_distribution_fee ?? 0 },
        { header: "Holding Fee (GST)", value: r => r.holding_fee_gst ?? "" },
        { header: "Cash + Liquid", value: r => r.cash_plus_liquid ?? "" },
        { header: "Client Type", value: r => r.client_type || "" },
        { header: "Status", value: r => (r.is_active ? "Active" : "Inactive") },
        { header: "At Risk", value: r => (isPmsCashAtRisk(r) ? "Yes" : "") },
    ];

    const downloadCSV = () => {
        if (!filteredAndSorted.length) return;
        // Quote anything non-numeric — client and distributor names contain commas.
        const esc = (v: string | number) =>
            typeof v === "number" ? String(v) : `"${v.replace(/"/g, '""')}"`;
        const csv = [
            csvColumns.map(c => esc(c.header)).join(","),
            ...filteredAndSorted.map(r => csvColumns.map(c => esc(c.value(r))).join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `management_fees_${selectedQuarter}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    const baseColumns: ColumnDef<ManagementFeeRow>[] = [
        {
            key: "client_code",
            header: "Client Code",
            sortable: true,
            width: "140px",
            render: row => (
                <>
                    {row.client_code}
                    {!row.is_active && <span className={styles['inactive-badge']}>Inactive</span>}
                </>
            ),
            renderFooter: () => "Totals",
        },
        {
            key: "client_name",
            header: "Client Name",
            sortable: true,
            width: "180px",
            render: row => (
                <span style={{ textDecoration: row.is_active ? undefined : "line-through" }}>
                    {row.client_name}
                </span>
            ),
            renderFooter: footerRows => (
                <span style={{ fontWeight: 400, fontSize: 11, color: "var(--text-dim)" }}>
                    {footerRows.length} client{footerRows.length === 1 ? "" : "s"} (all pages)
                </span>
            ),
        },
        { key: "scheme_code", header: "Scheme", sortable: true, width: "80px", render: row => row.scheme_code || "—" },
        { key: "distributor_name", header: "Distributor", sortable: true, width: "150px", render: row => row.distributor_name || "—" },
        { key: "management_fee_pct", header: "Mgmt Fee %", sortable: false, align: "right", width: "80px", render: row => row.management_fee_pct ?? "—" },
        {
            key: "distributor_share_pct",
            header: "Dist Share %",
            sortable: true,
            align: "right",
            width: "100px",
            render: row => row.distributor_share_pct != null
                ? String(parseFloat(row.distributor_share_pct.toFixed(2)))
                : "—",
        },
        { key: "nuvama_avg_aum", header: "Chargeable AUM", sortable: true, align: "right", width: "140px", render: row => fmt(row.nuvama_avg_aum) },
        { key: "nuvama_fee", header: "Nuvama Fee (₹)", sortable: true, align: "right", width: "130px", render: row => fmt(row.nuvama_fee) },
        {
            key: "nuvama_distribution_fee",
            header: "Nuvama Dist (₹)",
            sortable: true,
            align: "right",
            width: "125px",
            // Distribution fee is 0 (not null) for clients with no distributor.
            // The dist-zero class fades the 0 so it's visually distinct from an actual fee amount.
            render: row => {
                const val = row.nuvama_distribution_fee ?? 0;
                return <span className={val === 0 ? styles['dist-zero'] : undefined}>{fmt(val)}</span>;
            },
        },
        { key: "nuvama_fee_gst", header: "Nuvama Fee GST (₹)", sortable: true, align: "right", width: "145px", render: row => fmt(row.nuvama_fee_gst) },
        { key: "holding_avg_aum", header: "Holding AUM", sortable: true, align: "right", width: "130px", render: row => fmt(row.holding_avg_aum) },
        { key: "holding_fee", header: "Holding Fee (₹)", sortable: true, align: "right", width: "125px", render: row => fmt(row.holding_fee) },
        {
            key: "holding_distribution_fee",
            header: "Holding Dist (₹)",
            sortable: true,
            align: "right",
            width: "125px",
            // Same as nuvama_distribution_fee — fade 0 to signal no distributor.
            render: row => {
                const val = row.holding_distribution_fee ?? 0;
                return <span className={val === 0 ? styles['dist-zero'] : undefined}>{fmt(val)}</span>;
            },
        },
        { key: "holding_fee_gst", header: "Holding Fee GST (₹)", sortable: true, align: "right", width: "145px", render: row => fmt(row.holding_fee_gst) },
        { key: "cash_plus_liquid", header: "Cash + Liquid (₹)", sortable: true, align: "right", width: "140px", render: row => fmt(row.cash_plus_liquid) },
    ];

    // Attached by column key so a total can never land under the wrong header.
    const columns: ColumnDef<ManagementFeeRow>[] = baseColumns.map(col =>
        TOTAL_KEYS.includes(col.key as TotalKey)
            ? { ...col, renderFooter: rows => fmt(sumBy(rows, col.key as TotalKey)) }
            : col
    );

    const getRowStyle = (row: ManagementFeeRow): React.CSSProperties => ({
        backgroundColor: isPmsCashAtRisk(row) ? "var(--warn-soft)" : !row.is_active ? "var(--surface-2)" : undefined as any,
        opacity: row.is_active ? 1 : 0.6,
    });

    const filterSlot = (
        <>
            <InvestmentStrategySelect
                strategies={investmentStrategies}
                value={selectedStrategy}
                onChange={id => { setSelectedStrategy(id); setSelectedScheme(null); }}
            />

            <FilterAsyncSelect
                label="Scheme"
                value={selectedScheme}
                onChange={opt => { setSelectedScheme(opt); setCurrentPage(1); }}
                loadOptions={loadSchemeOptions}
            />

            <FilterSelect
                label="Quarter"
                options={quarterSelectOptions}
                value={quarterSelectOptions.find(o => o.value === selectedQuarter) ?? null}
                onChange={opt => setSelectedQuarter(opt?.value ?? getCurrentFYQuarter())}
            />

            <FilterSelect
                label="Client"
                options={clientOptions}
                value={selectedClient}
                onChange={opt => { setSelectedClient(opt); setCurrentPage(1); }}
                disabled={results.length === 0}
            />

            <FilterSelect
                label="Distributor"
                options={distributorSelectOptions}
                value={distributorSelectOptions.find(o => o.value === selectedDistributor) ?? null}
                onChange={opt => { setSelectedDistributor(opt?.value ?? ""); setCurrentPage(1); }}
                disabled={results.length === 0}
            />

            <div style={{ display: "flex", alignItems: "flex-end" }}>
                <Button variant="primary" onClick={fetchFees} disabled={loading}>
                    {loading ? "Loading..." : "Search"}
                </Button>
            </div>
        </>
    );

    const toolbarSlot = (isCurrentQuarter && atRiskCount > 0) ? (
        <div className={styles['mgmt-fees-toolbar']}>
            <Button
                size="sm"
                variant={lowCashOnly ? "warning" : "secondary"}
                aria-pressed={lowCashOnly}
                onClick={() => { setLowCashOnly(p => !p); setCurrentPage(1); }}
            >
                ⚠ {atRiskCount} at risk{lowCashOnly ? " — showing only" : ""}
            </Button>
        </div>
    ) : undefined;

    const footerSlot = (isCurrentQuarter && atRiskCount > 0) ? (
        <p className={styles['mgmt-fees-legend']}>
            * &ldquo;At risk&rdquo; = Cash + Liquid is below the quarterly fee (incl. GST) + {(FEE_BUFFER_PCT * 100).toFixed(0)}% buffer.
            Threshold = Fee (incl. GST) &times; {(1 + FEE_BUFFER_PCT).toFixed(2)}.
        </p>
    ) : undefined;

    const emptyMessage = !hasFetched
        ? "Select filters and click Search to view management fees."
        : "No data found for the selected filters.";

    return (
        <>
            <DataTable
                title="Management Fees"
                data={paginatedData}
                footerRows={filteredAndSorted}
                columns={columns}
                rowKey={row => row.client_code}
                rowStyle={getRowStyle}
                totalCount={filteredAndSorted.length}
                loading={loading}
                loadingMessage="Calculating management fees..."
                error={error}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                sortField={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                hideSearch
                filterSlot={filterSlot}
                toolbarSlot={toolbarSlot}
                onDownloadCSV={filteredAndSorted.length > 0 ? downloadCSV : undefined}
                emptyMessage={emptyMessage}
                onRowClick={handleRowClick}
                footerSlot={footerSlot}
                stickyColumns={3}
                headerActions={
                    <Button variant="secondary" onClick={() => setShowInvoiceFiles(true)}>
                        Bill / Invoice Files
                    </Button>
                }
            />
            {showInvoiceFiles && <FeeInvoiceFilesModal onClose={() => setShowInvoiceFiles(false)} />}
        </>
    );
};

export default ManagementFees;
